// app/src/dashboard/widgets/EconomicCalendarWidget.tsx
import { useState } from 'react'
import { useQuery } from 'wasp/client/operations'
import { getEconomicCalendar } from 'wasp/client/operations'
import { useTranslations } from '../../client/i18n/useTranslations'
import { Card } from '../../client/components/ui/card'
import { CardSectionHeader } from '../../client/components/ui/card-section-header'
import { format, addDays, subDays } from 'date-fns'
import { ChevronLeft, ChevronRight } from 'lucide-react'

type EconomicEvent = {
  id: string
  eventDate: string | Date
  country: string
  currency: string
  importance: string
  title: string
  actual: string | null
  forecast: string | null
  previous: string | null
  unit: string | null
}

const COUNTRY_FLAGS: Record<string, string> = {
  US: '🇺🇸', EU: '🇪🇺', GB: '🇬🇧', JP: '🇯🇵',
  CA: '🇨🇦', AU: '🇦🇺', NZ: '🇳🇿', CH: '🇨🇭',
  CN: '🇨🇳', DE: '🇩🇪', FR: '🇫🇷',
}

function ImportanceDot({ importance }: { importance: string }) {
  return (
    <span className={`inline-block w-1.5 h-1.5 rounded-full ${
      importance === 'high' ? 'bg-destructive'
      : importance === 'medium' ? 'bg-orange-400'
      : 'bg-muted-foreground'
    }`} />
  )
}

function ActualValue({ actual, forecast }: { actual: string | null; forecast: string | null }) {
  if (!actual) return <span className="text-muted-foreground/40 italic">—</span>

  const numActual = parseFloat(actual)
  const numForecast = parseFloat(forecast ?? '')
  let color = ''
  if (!isNaN(numActual) && !isNaN(numForecast)) {
    color = numActual > numForecast ? 'text-primary' : numActual < numForecast ? 'text-destructive' : ''
  }

  return <span className={`font-mono font-medium ${color}`}>{actual}</span>
}

type ImportanceFilter = 'low' | 'medium' | 'high'

export function EconomicCalendarWidget() {
  const t = useTranslations()
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [activeFilters, setActiveFilters] = useState<ImportanceFilter[]>(['high', 'medium'])

  const { data: rawEvents, isLoading } = useQuery(getEconomicCalendar, {
    date: selectedDate.toISOString(),
    importance: activeFilters,
  })
  const events = rawEvents as EconomicEvent[] | undefined

  const toggleFilter = (f: ImportanceFilter) => {
    setActiveFilters((prev) =>
      prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]
    )
  }

  const filterLabels: Record<ImportanceFilter, string> = {
    high: t.marketDataWidgets.economicCalendar.filterHigh,
    medium: t.marketDataWidgets.economicCalendar.filterMedium,
    low: t.marketDataWidgets.economicCalendar.filterLow,
  }

  const filterColors: Record<ImportanceFilter, string> = {
    high: 'bg-destructive/20 text-destructive',
    medium: 'bg-orange-500/20 text-orange-400',
    low: 'bg-muted text-muted-foreground',
  }

  return (
    <Card className="flex flex-col h-full overflow-hidden">
      <CardSectionHeader
        label={t.marketDataWidgets.economicCalendar.title}
        isDark={false}
        compact
        rightContent={
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1">
              <button onClick={() => setSelectedDate(subDays(selectedDate, 1))} className="p-0.5 hover:text-primary">
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              <span className="text-xs text-muted-foreground w-20 text-center">
                {format(selectedDate, 'dd/MM/yy')}
              </span>
              <button onClick={() => setSelectedDate(addDays(selectedDate, 1))} className="p-0.5 hover:text-primary">
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="flex gap-1">
              {(['high', 'medium', 'low'] as ImportanceFilter[]).map((f) => (
                <button
                  key={f}
                  onClick={() => toggleFilter(f)}
                  className={`text-[9px] px-1.5 py-0.5 rounded transition-colors ${
                    activeFilters.includes(f) ? filterColors[f] : 'text-muted-foreground/40'
                  }`}
                >
                  {filterLabels[f]}
                </button>
              ))}
            </div>
          </div>
        }
      />

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex flex-col gap-1 p-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="animate-pulse h-8 rounded bg-muted" />
            ))}
          </div>
        ) : !events || events.length === 0 ? (
          <div className="flex items-center justify-center h-full text-muted-foreground text-sm p-4">
            {t.marketDataWidgets.economicCalendar.noEvents}
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="sticky top-0 z-10 bg-card border-b border-border">
              <tr>
                <th className="px-2 py-1 text-left text-muted-foreground font-normal w-12">Heure</th>
                <th className="px-1 py-1 text-left text-muted-foreground font-normal w-8" />
                <th className="px-2 py-1 text-left text-muted-foreground font-normal">Événement</th>
                <th className="px-2 py-1 text-right text-muted-foreground font-normal w-14">{t.marketDataWidgets.economicCalendar.actual}</th>
                <th className="px-2 py-1 text-right text-muted-foreground font-normal w-14">{t.marketDataWidgets.economicCalendar.forecast}</th>
                <th className="px-2 py-1 text-right text-muted-foreground font-normal w-14">{t.marketDataWidgets.economicCalendar.previous}</th>
              </tr>
            </thead>
            <tbody>
              {(events as EconomicEvent[]).map((event) => (
                <tr
                  key={event.id}
                  className={`border-b border-border/40 hover:bg-accent/30 ${!event.actual ? 'opacity-60' : ''}`}
                >
                  <td className="px-2 py-1.5 font-mono text-muted-foreground">
                    {format(new Date(event.eventDate), 'HH:mm')}
                  </td>
                  <td className="px-1 py-1.5 text-center">
                    <div className="flex items-center gap-0.5">
                      <ImportanceDot importance={event.importance} />
                      <span className="text-[9px]">{COUNTRY_FLAGS[event.country] ?? event.country}</span>
                    </div>
                  </td>
                  <td className="px-2 py-1.5 max-w-0">
                    <span className="block truncate">{event.title}</span>
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <ActualValue actual={event.actual} forecast={event.forecast} />
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono text-muted-foreground">
                    {event.forecast ?? '—'}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono text-muted-foreground">
                    {event.previous ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Card>
  )
}
